import { cssVar, Flex, Box, Icon, Text } from '@cad-arc';
import { CliArgumentEnumType, CliAutocompleteOption, CliResource } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useApiClient } from '@m43/core';
import { CommandLineEditorStore } from './command-line-editor-store';
import { LocationSearchHelper } from './location-search-helper';
import { useLocationApi } from '../location-select/hooks/use-location-api';
import { HelpWindow } from './help-window';
import { matchSorter } from 'match-sorter';
import { HospitalStatusTag } from '../common/components/hospital-status-tag';
import { WorkspacePanel } from '../panels/workspace-panel';

const AUTOCOMPLETE_BORDER_WIDTH = 2;
const AUTOCOMPLETE_ITEM_HEIGHT = 36;
const AUTOCOMPLETE_MAX_HEIGHT = AUTOCOMPLETE_ITEM_HEIGHT * 4;

interface AutocompleteOptionRowProps {
    onClick: () => void;
    onMouseEnter: () => void;
    item: CliAutocompleteOption;
    isSelected: boolean;
    isHospital: boolean;
}

function AutocompleteSubtitle({
    item,
    isHospital,
}: {
    item: CliAutocompleteOption;
    isHospital: boolean;
}) {
    if (!item.subtitle) {
        return null;
    }

    return (
        <>
            {isHospital ? (
                <HospitalStatusTag
                    hospitalStatusGlobalAttributeId={item.globalAttributeId}
                    hospitalStatus={item.subtitle}
                />
            ) : (
                <Text variant="headingSm" color="inherit" noOfLines={2}>
                    {item.subtitle}
                </Text>
            )}
        </>
    );
}

export function AutocompleteOptionRow({
    onClick,
    onMouseEnter,
    item,
    isSelected,
    isHospital,
}: AutocompleteOptionRowProps): React.ReactElement {
    return (
        <Flex
            direction="row"
            px="4"
            alignItems="center"
            h={AUTOCOMPLETE_ITEM_HEIGHT}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            cursor="pointer"
            w="full"
            bg={isSelected ? 'selected.default' : 'surface.canvas'}
            color={isSelected ? 'selected.content' : 'text.primary'}
        >
            <Box
                flex={2}
                style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
            >
                <Text
                    color="inherit"
                    style={{
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                    variant="headingSm"
                >
                    {item.title}
                </Text>
            </Box>

            <Box flex={3}>
                <AutocompleteSubtitle item={item} isHospital={isHospital} />
            </Box>

            {item.secondarySubtitle ? (
                <Box flex={2}>
                    <Text color="inherit" variant="headingSm">
                        {item.secondarySubtitle}
                    </Text>
                </Box>
            ) : null}

            <Flex
                title="Cleared event"
                w="20px"
                flexShrink={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                {item.eventDetails?.isActive === false ? (
                    <Icon
                        icon="Closed"
                        aria-label="Cleared event"
                        color={isSelected ? 'brand.content' : 'icon.default'}
                    />
                ) : null}
            </Flex>
        </Flex>
    );
}

export const Autocomplete = observer(function Autocomplete(props: {
    editorStore: CommandLineEditorStore;
}): React.ReactElement {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const { editorStore } = props;
    const apiClient = useApiClient();

    const query = editorStore.helperSearchText;
    const segmentType = editorStore.getCurrentSegment?.segmentType;
    const selectedIndex = editorStore.helperState?.selectedIndex;

    const rows = editorStore.helperState?.options ?? [];

    const totalCount = rows.length;

    const enabled =
        !!segmentType &&
        !!editorStore.helperState?.activeHelper &&
        editorStore.helperState.activeHelper !== 'HELP' &&
        editorStore.helperState.activeHelper === segmentType;

    useEffect(() => {
        editorStore.isAutocompleteLoading = enabled;
    }, [editorStore, enabled]);

    useQuery(
        ['command-line-search', segmentType, query],
        ({ queryKey }) => {
            editorStore.isAutocompleteLoading = true;

            return apiClient
                .request<CliResource.Autocomplete>({
                    method: 'POST',
                    url: `cli/autocomplete`,
                    // Intentionally supressing errors here to maintain existing behavior/logic.
                    // Down the line we'll want to conform to some error handling logic but it will
                    // require additional design and product work - NH
                    shouldFailSilently: true,
                    data: {
                        cliArgument: queryKey[1] as CliArgumentEnumType,
                        cliCommand: editorStore.activeCommandSegment?.segmentData
                            .cliArgument as CliArgumentEnumType,
                        query: queryKey[2] ?? '',
                    },
                })
                .then((resp) => {
                    return resp.data.data?.cliAutocompleteOptions;
                });
        },
        {
            enabled,
            onSuccess(cliAutocompleteOptions) {
                if (cliAutocompleteOptions) {
                    editorStore.setHelperOptions(
                        getSortedOptions(cliAutocompleteOptions).reverse()
                    );
                }
            },
            onSettled() {
                editorStore.isAutocompleteLoading = false;
            },
        }
    );

    const getSortedOptions = (cliAutocompleteOptions: CliAutocompleteOption[]) => {
        if (segmentType === 'EVENT') {
            // events have a specific order where entries ending with the query goes first, BE do this sorting
            return cliAutocompleteOptions;
        }
        return matchSorter(cliAutocompleteOptions, query ?? '', {
            keys: ['title', 'subtitle', 'secondarySubtitle'],
            baseSort: (a, b) => a.item.title.localeCompare(b.item.title),
            threshold: matchSorter.rankings.NO_MATCH,
        });
    };

    const renderItemContent = (index: number): React.ReactElement => {
        const item = rows[index];

        return (
            <AutocompleteOptionRow
                item={item}
                onClick={() => {
                    editorStore.externalSelectOption(item);
                }}
                onMouseEnter={() => editorStore.setHelperSelectedIndex(index)}
                isSelected={index === selectedIndex}
                isHospital={editorStore.helperState?.activeHelper === 'HOSPITAL'}
            />
        );
    };

    const innerHeight = totalCount * AUTOCOMPLETE_ITEM_HEIGHT;
    const listHeight =
        (innerHeight > AUTOCOMPLETE_MAX_HEIGHT ? AUTOCOMPLETE_MAX_HEIGHT : innerHeight) +
        AUTOCOMPLETE_BORDER_WIDTH * 2;

    useEffect(() => {
        setTimeout(() => {
            if (typeof selectedIndex !== 'undefined') {
                virtuosoRef.current?.scrollIntoView({ index: selectedIndex, behavior: 'auto' });
            }
        }, 0);
    }, [selectedIndex, virtuosoRef]);

    if (!totalCount) {
        return <></>;
    }

    return (
        <Flex w="100%" justifyContent="center">
            <Virtuoso
                style={{
                    borderRadius: 'var(--arc-radii-base)',
                    borderWidth: AUTOCOMPLETE_BORDER_WIDTH + 'px',
                    borderStyle: 'solid',
                    borderColor: 'var(--cad-colors-command-line-popout-border)',
                    backgroundColor: cssVar('arc.colors.surface.canvas'),
                    display: 'block',
                    height: listHeight,
                    width: '75%',
                    minWidth: '250px',
                }}
                ref={virtuosoRef}
                totalCount={totalCount}
                overscan={10}
                itemContent={renderItemContent}
                initialTopMostItemIndex={totalCount - 1}
            />
        </Flex>
    );
});

export const CommandLineHelpers = observer(function CommandLineHelpers(props: {
    editorStore: CommandLineEditorStore;
    locationApi: ReturnType<typeof useLocationApi>;
    historyHeight: number;
    panel: WorkspacePanel;
}): React.ReactElement {
    const { editorStore, historyHeight } = props;

    if (editorStore.helperState?.activeHelper === 'LOCATION') {
        return <LocationSearchHelper locationApi={props.locationApi} editorStore={editorStore} />;
    }

    if (editorStore.helperState?.activeHelper === 'HELP') {
        return <HelpWindow historyHeight={historyHeight} editorStore={editorStore} />;
    }
    return <Autocomplete editorStore={editorStore} />;
});
